/**
 * Whitelabel Integration Module
 * Provides access to whitelabel configuration throughout the application
 */

import { 
  WHITELABEL_CONFIG, 
  validateWhitelabelConfig, 
  getWhitelabelAssetUrl,
  getConfigWithFallbacks,
  validateAssetsAvailability,
  getAssetUrlWithFallback
} from '../../whitelabel/config'
import type { WhitelabelConfig } from '../../whitelabel/config'

/**
 * Get the current whitelabel configuration with development fallbacks
 * This is the main entry point for accessing whitelabel settings
 */
export function getWhitelabelConfig(): WhitelabelConfig {
  return getConfigWithFallbacks()
}

/**
 * Get a whitelabel asset URL
 * Automatically resolves asset paths for development and production
 */
export function getAssetUrl(assetPath: string): string {
  return getWhitelabelAssetUrl(assetPath)
}

/**
 * Validate the current whitelabel configuration
 * Should be called during application initialization
 */
export function validateConfiguration(): boolean {
  const errors = validateWhitelabelConfig(WHITELABEL_CONFIG)
  
  if (errors.length > 0) {
    console.error('Whitelabel configuration validation failed:')
    errors.forEach(error => console.error(`  • ${error}`))
    return false
  }
  
  console.log('✅ Whitelabel configuration validated successfully')
  return true
}

/**
 * Initialize whitelabel configuration
 * Call this during app startup
 */
export function initializeWhitelabel(): void {
  console.log('🎨 Initializing whitelabel configuration...')
  
  const isValid = validateConfiguration()
  if (!isValid) {
    console.warn('⚠️ Whitelabel configuration has errors, using defaults')
  }
  
  // Check for asset availability and show development warnings
  const assetValidation = validateAssetsAvailability()
  if (assetValidation.hasIssues && import.meta.env.DEV) {
    console.warn('🖼️ Asset availability warnings:')
    assetValidation.warnings.forEach(warning => console.warn(`  • ${warning}`))
  }
  
  // Log configuration summary
  const config = getWhitelabelConfig()
  console.log('🏷️ App Name:', config.appName)
  console.log('🎨 Primary Color:', config.primaryColor)
  console.log('✨ Features:', Object.keys(config.features).filter(key => 
    config.features[key as keyof typeof config.features]
  ).join(', '))
  
  if (import.meta.env.DEV) {
    console.log('🖼️ Assets:')
    console.log('  • Logo:', config.logoUrl)
    console.log('  • Small Logo:', config.logoSmallUrl) 
    console.log('  • Favicon:', config.faviconUrl)
  }
}

// Re-export types and utilities for convenience
export type { WhitelabelConfig }
export { validateWhitelabelConfig, getAssetUrlWithFallback, validateAssetsAvailability }

// Export the configuration for direct access (use getWhitelabelConfig() instead)
export { WHITELABEL_CONFIG }

// Default export for easy importing
export default {
  config: getWhitelabelConfig,
  getAssetUrl,
  getAssetUrlWithFallback,
  validate: validateConfiguration,
  validateAssets: validateAssetsAvailability,
  initialize: initializeWhitelabel
}